import { ENDPOINTS } from 'config/endpoints';
import { useQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { NextTaskSearch } from 'crono-fe-common/types/DTO/nextTaskSearch';

export default function useSearchNextTask(
  searchParameters: NextTaskSearch,
  enabled = true,
  // Scopes the cached response to a single flow run. A run started from the
  // first pending task parks this query on `id: null` for as long as that task
  // is being executed, so `cacheTime` never collects it — and a restart with
  // the same filters hashes to the very same key. Without this the restart is
  // served the response the previous run already consumed (a task the BE may
  // have stopped since) and never asks again.
  flowRunId?: number,
) {
  const request: Request = {
    url: ENDPOINTS.task.next,
    config: {
      method: 'post',
      //If no date is passed I get the next of today
      data: { data: { date: new Date(), ...searchParameters } },
    },
  };

  const requestConfig = {
    queryKey: [ENDPOINTS.task.next, searchParameters, flowRunId],

    queryFn: () => client(request),
    enabled,
    // This query drives the multi-task flow execution: each response must be
    // consumed exactly once, and re-fetching behind the flow's back can replay
    // an already-handled id. Never refetch while active, and drop the response
    // as soon as the flow moves on so a later (re)start or back-navigation
    // always fetches fresh.
    staleTime: Infinity,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  };

  return useQuery<
    Response<TaskTodo | null> & {
      data: {
        nextId: number | null;
        count: number;
      };
    },
    CronoError
  >(requestConfig);
}
